import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuthRefresh } from '../lib/useAuthRefresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PhishPassContext = createContext();

export const usePhishPass = () => {
  const ctx = useContext(PhishPassContext);
  if (!ctx) throw new Error('usePhishPass must be inside PhishPassProvider');
  return ctx;
};

export const PhishPassProvider = ({ children, token }) => {
  // Silent access-token refresh — see useAuthRefresh for the rationale.
  useAuthRefresh(token, { backendUrl: BACKEND_URL, apiPrefix: '/api/phishpass' });

  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const api = `${BACKEND_URL}/api/phishpass`;

  const fetchTemplates = useCallback(async () => {
    try {
      const r = await axios.get(`${api}/templates`, { headers });
      setTemplates(r.data || []);
      return r.data || [];
    } catch (e) { console.error(e); return []; }
  }, [token]);

  const getTemplate = useCallback(async (id) => {
    try { const r = await axios.get(`${api}/templates/${id}`, { headers }); return r.data; }
    catch (e) { return null; }
  }, [token]);

  const createTemplate = useCallback(async (data) => {
    try { const r = await axios.post(`${api}/templates`, data, { headers }); await fetchTemplates(); return { success: true, data: r.data }; }
    catch (e) { return { success: false, error: e.response?.data?.detail || 'Failed to create template' }; }
  }, [token, fetchTemplates]);

  const updateTemplate = useCallback(async (id, data) => {
    try { const r = await axios.put(`${api}/templates/${id}`, data, { headers }); await fetchTemplates(); return { success: true, data: r.data }; }
    catch (e) { return { success: false, error: e.response?.data?.detail || 'Failed to update template' }; }
  }, [token, fetchTemplates]);

  const deleteTemplate = useCallback(async (id) => {
    try { await axios.delete(`${api}/templates/${id}`, { headers }); await fetchTemplates(); return { success: true }; }
    catch (e) { return { success: false, error: e.response?.data?.detail || 'Failed to delete template' }; }
  }, [token, fetchTemplates]);

  const previewTemplate = useCallback(async (id) => {
    try { const r = await axios.post(`${api}/templates/${id}/preview`, {}, { headers }); return r.data; }
    catch (e) { return null; }
  }, [token]);

  const fetchEmployees = useCallback(async () => {
    try {
      const r = await axios.get(`${api}/employees-lookup`, { headers });
      setEmployees(r.data || []);
      return r.data || [];
    } catch (e) { return []; }
  }, [token]);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${api}/campaigns`, { headers });
      setCampaigns(r.data || []);
      return r.data || [];
    } catch (e) { return []; } finally { setLoading(false); }
  }, [token]);

  const getCampaign = useCallback(async (id) => {
    try { const r = await axios.get(`${api}/campaigns/${id}`, { headers }); return r.data; }
    catch (e) { return null; }
  }, [token]);

  const sendCampaign = useCallback(async (payload) => {
    try { const r = await axios.post(`${api}/campaigns/send`, payload, { headers }); return { success: true, data: r.data }; }
    catch (e) { return { success: false, error: e.response?.data?.detail || 'Send failed' }; }
  }, [token]);

  const deleteCampaign = useCallback(async (id) => {
    try { await axios.delete(`${api}/campaigns/${id}`, { headers }); await fetchCampaigns(); return { success: true }; }
    catch (e) { return { success: false, error: e.response?.data?.detail || 'Delete failed' }; }
  }, [token, fetchCampaigns]);

  const resetCampaignClicks = useCallback(async (id) => {
    try { const r = await axios.post(`${api}/campaigns/${id}/reset-clicks`, {}, { headers }); return { success: true, data: r.data }; }
    catch (e) { return { success: false, error: e.response?.data?.detail || 'Reset failed' }; }
  }, [token]);

  const fetchRecipientsRisk = useCallback(async () => {
    try { const r = await axios.get(`${api}/recipients/risk`, { headers }); return r.data || []; }
    catch (e) { return []; }
  }, [token]);

  const markTrained = useCallback(async (email) => {
    try { await axios.post(`${api}/recipients/mark-trained`, { email }, { headers }); return { success: true }; }
    catch (e) { return { success: false }; }
  }, [token]);

  const getDashboardStats = useCallback(async () => {
    try { const r = await axios.get(`${api}/dashboard/stats`, { headers }); return r.data; }
    catch (e) { return null; }
  }, [token]);

  const getSettings = useCallback(async () => {
    try { const r = await axios.get(`${api}/settings`, { headers }); return r.data; }
    catch (e) { return null; }
  }, [token]);

  const updateSettings = useCallback(async (data) => {
    try { const r = await axios.put(`${api}/settings`, data, { headers }); return { success: true, data: r.data }; }
    catch (e) { return { success: false, error: e.response?.data?.detail || 'Save failed' }; }
  }, [token]);

  const getAuditLogs = useCallback(async () => {
    try { const r = await axios.get(`${api}/audit-logs`, { headers }); return r.data || []; }
    catch (e) { return []; }
  }, [token]);

  const value = {
    templates, campaigns, employees, loading,
    fetchTemplates, getTemplate, createTemplate, updateTemplate, deleteTemplate, previewTemplate,
    fetchEmployees, fetchCampaigns, getCampaign, sendCampaign, deleteCampaign,
    fetchRecipientsRisk, markTrained,
    getDashboardStats, getSettings, updateSettings, getAuditLogs,
    resetCampaignClicks,
  };

  return <PhishPassContext.Provider value={value}>{children}</PhishPassContext.Provider>;
};

export default PhishPassContext;
